import { removeAutopilotEmoji } from "#webapp/src/util/event.util";
import getDuration from "#webapp/src/util/getDuration";
import getRecurranceEnum from "#webapp/src/util/getRecurranceEnum";
import getTimeDescription from "#webapp/src/util/getTimeDescription";
import isTimeRangeFlexibilityEditable from "#webapp/src/util/isTimeRangeFlexibilityEditable";
import { getValue } from "@clockwise/client-commons/src/util/errorable.util";
import { RecurrenceType } from "@clockwise/schema";
import { FlexMeetingSlideQuery } from "../__generated__/FlexMeetingSlide.generated";

type FlexibilityType =
  | {
      __typename: "EventFlexibility";
    }
  | {
      __typename: "NotFlexible";
    };
export type RecurringEventGQL = {
  __typename: "RecurringEvent";
  timeDescription: string | null;
  anInstanceId: any;
  recurrenceType: RecurrenceType;
  id: any;
  title: string;
  duration: any;
  flexibilityConstraints: {
    __typename: "FlexibilityConstraints";
    allowedToModify: boolean | null;
  } | null;
  flexibility:
    | {
        __typename: "EventFlexibility";
      }
    | {
        __typename: "NotFlexible";
      };
};

export type SuggestedEventGQL = {
  __typename: "CalendarEvent";
  timeDescription: string | null;
  id: any;
  title: string;
  flexibilityConstraints: {
    __typename: "FlexibilityConstraints";
    allowedToModify: boolean | null;
  } | null;
  flexibility:
    | {
        __typename: "EventFlexibility";
      }
    | {
        __typename: "NotFlexible";
      };
};

const filterAllowedToModify = (event: RecurringEventGQL | SuggestedEventGQL) =>
  event?.flexibilityConstraints?.allowedToModify === true;

const filterNotFlexible = (event: RecurringEventGQL | SuggestedEventGQL) =>
  event.flexibility.__typename === "NotFlexible";

const getFlexibilityBoolean = (flexibility: FlexibilityType) => {
  return flexibility.__typename === "NotFlexible" ? false : true;
};

export const parseSuggestions = (
  data: FlexMeetingSlideQuery | undefined,
  showAlreadyEnabled: boolean,
) => {
  const myOneOnOnes = getValue(data?.org, "Org")?.flexibleMeetingsDashboard?.myOneOnOnes ?? [];
  const myOneOnOnesIdList = myOneOnOnes.map((event) => event.anInstanceId);

  const suggestedFlexibleMeetings =
    getValue(data?.org, "Org")?.flexibleMeetingsDashboard?.suggestedFlexibleMeetings ?? [];

  const dedupedSuggestionsFromOneOnOnes =
    suggestedFlexibleMeetings.filter(
      (suggestion) => myOneOnOnesIdList.includes(suggestion.id) === false,
    ) ?? [];

  const filteredAllowedToModify = dedupedSuggestionsFromOneOnOnes.filter(filterAllowedToModify);
  const filteredSuggestedFlexMeetings = showAlreadyEnabled
    ? filteredAllowedToModify
    : filteredAllowedToModify.filter(filterNotFlexible);

  return (
    filteredSuggestedFlexMeetings?.map((datum) => ({
      id: datum.id,
      duration: undefined,
      conflicted: false,
      editable: isTimeRangeFlexibilityEditable(datum),
      flexible: showAlreadyEnabled ? getFlexibilityBoolean(datum.flexibility) : true,
      title: removeAutopilotEmoji(datum.title) || "Untitled Event",
      recurrence: null, // hard coding so it appears in the correct column
      recurrenceText: getTimeDescription(datum),
    })) || []
  );
};
export const parseOneOnOnes = (
  data: FlexMeetingSlideQuery | undefined,
  showAlreadyEnabled: boolean,
) => {
  const myOneOnOnes = getValue(data?.org, "Org")?.flexibleMeetingsDashboard?.myOneOnOnes ?? [];
  const filteredAllowedToModify = myOneOnOnes.filter(filterAllowedToModify);
  const filteredOneOnOnes = showAlreadyEnabled
    ? filteredAllowedToModify
    : filteredAllowedToModify.filter(filterNotFlexible);

  return (
    filteredOneOnOnes?.map((datum) => ({
      id: datum.id,
      duration: getDuration(datum),
      conflicted: false,
      editable: isTimeRangeFlexibilityEditable(datum),
      flexible: showAlreadyEnabled ? getFlexibilityBoolean(datum.flexibility) : true,
      title: removeAutopilotEmoji(datum.title) || "Untitled Event",
      recurrence: getRecurranceEnum(datum),
      recurrenceText: getTimeDescription(datum),
    })) || []
  );
};
